<template>
  <el-dialog :title="title" :visible="true" width="90%" @close="$emit('close')">
    <p>{{ identity }}</p>
    <el-alert v-if="error" :title="error" type="error" :closable="false" />
    <el-table v-loading="loading" :data="rows" border>
      <el-table-column
        v-for="column in columns"
        :key="column[0]"
        :prop="column[0]"
        :label="column[1]"
        min-width="145"
      >
        <template slot-scope="scope">
          <span
            v-if="column[2]"
            data-test="quantity-change"
            :class="changeClass(scope.row[column[2]], scope.row[column[3]])"
            >{{
              quantityText(scope.row[column[2]], scope.row[column[3]])
            }}</span
          >
          <span v-else-if="column[0] === 'changeType'">{{
            changeTypeName(scope.row.changeType)
          }}</span>
          <span v-else-if="column[0] === 'sourceType'">{{
            sourceTypeName(scope.row.sourceType)
          }}</span>
          <span v-else-if="column[0] === 'batchId'">{{ stock.batchId }}</span>
          <span v-else>{{ scope.row[column[0]] }}</span>
        </template>
      </el-table-column>
    </el-table>
    <el-pagination
      :current-page="pageIndex"
      :page-size="10"
      :total="total"
      layout="total, prev, pager, next"
      @current-change="changePage"
    />
    <span slot="footer"
      ><el-button @click="fetch">刷新</el-button
      ><el-button @click="$emit('close')">关闭</el-button></span
    >
  </el-dialog>
</template>

<script>
import { AccountBookChangeType, ExpiryChangeType } from "./receiptEnums";
import api from "@/api/warehouseReceipt";

const SOURCE_TYPE_NAMES = {
  OUTBOUND_ORDER: "出库单",
  RWA_LOCK: "锁仓",
  RWA_PLEDGE_UNLOCK: "质押解锁",
  RWA_TRANSFER: "货权转移",
  RWA_TRANSFER_UNLOCK: "货权转移解锁"
};

const CHANGE_TYPE_NAMES = {
  accountBook: {
    [AccountBookChangeType.INBOUND_RELEASE]: "入库放行",
    [AccountBookChangeType.OUTBOUND_FREEZE]: "出库申报冻结",
    [AccountBookChangeType.OUTBOUND_RELEASE]: "出库放行",
    [AccountBookChangeType.OUTBOUND_CANCEL]: "出库取消"
  },
  expiry: {
    [ExpiryChangeType.RWA_PLEDGE_UNLOCK_CONFIRM]: "质押解锁确认",
    [ExpiryChangeType.RWA_LOCK_APPLY]: "锁仓申请预占",
    [ExpiryChangeType.RWA_LOCK_CONFIRM]: "锁仓确认",
    [ExpiryChangeType.RWA_LOCK_REJECT]: "锁仓驳回释放",
    [ExpiryChangeType.INBOUND_RECEIPT]: "入库收货",
    [ExpiryChangeType.OUTBOUND_OCCUPY]: "出库占用",
    [ExpiryChangeType.OUTBOUND_RELEASE]: "出库取消释放",
    [ExpiryChangeType.OUTBOUND_STOCKOUT]: "出库实扣",
    [ExpiryChangeType.OUTBOUND_FINISH_RELEASE]: "出库余量释放"
  }
};

export default {
  props: {
    kind: { type: String, required: true },
    recordType: { type: String, required: true },
    stock: { type: Object, required: true }
  },
  data() {
    return {
      rows: [],
      loading: false,
      error: "",
      pageIndex: 1,
      total: 0,
      requestId: 0
    };
  },
  computed: {
    title() {
      return this.recordType === "ledger" ? "库存流水" : "当前占用记录";
    },
    identity() {
      return (
        this.stock.customerId +
        " / " +
        (this.kind === "accountBook"
          ? this.stock.accountBookNo + " / " + this.stock.itemNo
          : this.stock.stockId + " / " + this.stock.batchId)
      );
    },
    columns() {
      if (this.recordType === "occupancy")
        return this.kind === "accountBook"
          ? [
              ["outboundOrderId", "出库单号"],
              ["hezhuOrderId", "核注单号"],
              ["itemNo", "商品料号"],
              ["frozenQty", "当前申报冻结数量"],
              ["gmtCreate", "创建时间"]
            ]
          : [
              ["sourceType", "来源类型"],
              ["sourceId", "来源单号 / 任务号"],
              ["batchId", "WMS批次号"],
              ["occupiedQty", "当前占用数量"]
            ];
      const quantities = [
        ["TotalQty", "总量"],
        ["AvailableQty", "可用"],
        [this.kind === "accountBook" ? "FrozenQty" : "OccupiedQty", "占用"]
      ];
      if (this.kind === "expiry") quantities.push(["LockedQty", "锁定"]);
      return [
        ["changeType", "变动类型"],
        ["qty", "变动数量"]
      ].concat(
        this.kind === "expiry"
          ? [
              ["sourceType", "来源类型"],
              ["sourceId", "来源任务号"]
            ]
          : [],
        quantities.map(([key, label]) => [
          "after" + key,
          label + "变化",
          "before" + key,
          "after" + key
        ]),
        [
          ["inboundOrderId", "入库单号"],
          ["outboundOrderId", "出库单号"],
          [
            this.kind === "accountBook" ? "hezhuOrderId" : "wmsOrderNo",
            this.kind === "accountBook" ? "核注单号" : "WMS单号"
          ],
          ["gmtCreate", "时间"]
        ]
      );
    }
  },
  created() {
    this.fetch();
  },
  beforeDestroy() {
    this.requestId++;
  },
  methods: {
    sourceTypeName(value) {
      return SOURCE_TYPE_NAMES[value] || value;
    },
    changeTypeName(value) {
      return CHANGE_TYPE_NAMES[this.kind][value] || value;
    },
    quantityText(before, after) {
      return (
        (before == null ? "—" : before) + " → " + (after == null ? "—" : after)
      );
    },
    changeClass(before, after) {
      const difference = Number(after) - Number(before);
      if (difference > 0) return "stock-change--increase";
      if (difference < 0) return "stock-change--decrease";
      return "";
    },
    changePage(page) {
      this.pageIndex = page;
      this.fetch();
    },
    async fetch() {
      const id = ++this.requestId;
      this.rows = [];
      this.total = 0;
      this.loading = true;
      this.error = "";
      const request = { customerId: this.stock.customerId };
      if (this.kind === "accountBook")
        Object.assign(request, {
          accountBookNo: this.stock.accountBookNo,
          itemNo: this.stock.itemNo
        });
      else if (this.recordType === "occupancy")
        request.stockId = this.stock.stockId;
      else request.batchId = this.stock.batchId;
      try {
        const res = await api[this.kind][this.recordType]({
          ...request,
          pageIndex: this.pageIndex,
          pageSize: 10
        });
        if (id !== this.requestId) return;
        this.rows = res.items || [];
        this.total = res.page.totalCount;
      } catch (error) {
        if (id === this.requestId) this.error = "记录加载失败，请重试";
      } finally {
        if (id === this.requestId) this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.stock-change--increase {
  color: #67c23a;
}

.stock-change--decrease {
  color: #f56c6c;
}
</style>
